import { ChevronDownIcon } from '@heroicons/react/outline'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import useSWR from 'swr'
import { CustomerAPI, CustomerFetcher } from '../apis/CustomerAPI'
import { UserStore } from '../state/UserStore'
import { asyncUpdateCustomerOrders } from '../helpers/slug'
import { Menu } from '@headlessui/react'
import { logout } from '../helpers/account'
import { _isImpersonated, isCustomerUnAuthorized, log } from '../helpers/utils'
import Loader from './Loader'
import { useRouter } from 'next/router'

import UserIcon from '../Icons/header/user.svg'

const MenuItemAction = ({ active, handleLogout, label, url }) => {
    const isLogout = label === 'Logout'

    if (isLogout && _isImpersonated()) return null

    return label === 'Logout' ? (
        <button
            className={`${
                active ? 'bg-gray-100' : ''
            } group flex text-sm w-full relative items-center rounded-md px-3 py-2 hover:bg-gray-50 font-medium text-olm-black`}
            onClick={(e) => {
                e.preventDefault()
                handleLogout()
            }}>
            {label}
        </button>
    ) : (
        <Link href={url}>
            <a
                className={`${
                    active ? 'bg-gray-100' : ''
                } group flex text-sm w-full relative items-center rounded-md px-3 py-2 hover:bg-gray-50 font-medium text-olm-black`}>
                {label}
            </a>
        </Link>
    )
}

function LoginButton({ user = null }) {
    const { data: customer, error } = useSWR(
        CustomerAPI.getCustomer,
        CustomerFetcher,
    )

    // Start with consistent initial state
    const [isLogin, setIsLogin] = useState(false)
    const [isMounted, setIsMounted] = useState(false)
    const [loading, setLoading] = useState(false)

    const updateCustomer = UserStore((state) => state.updateCustomer)
    const router = useRouter()

    const link = isLogin ? '/my-account' : '/login'

    const isProofPage = router?.asPath?.includes('/proofs')

    const handleCustomerLogin = async () => {
        if (!error && !customer) return
        if (isCustomerUnAuthorized(customer) && !isProofPage) {
            const shouldRedirect = router?.asPath?.includes('/my-account')
            return await logout(shouldRedirect)
        }
        updateCustomer(customer)
        asyncUpdateCustomerOrders()
    }

    log('customer', customer, _isImpersonated())

    // Handle client-side mounting and state initialization
    useEffect(() => {
        setIsMounted(true)
        setIsLogin(Boolean(user?.ID))
    }, [user?.ID])

    useEffect(() => {
        if (isMounted) {
            handleCustomerLogin()
        }
    }, [customer, isMounted])

    const menuItems = [
        { Orders: '/my-account/orders' },
        { Subscriptions: '/my-account/subscriptions' },
        { Uploads: '/my-account/uploads' },
        { 'Account Details': '/my-account/account-details' },
        {
            'Return Address Profiles': '/my-account/return-address-profiles',
        },
        { Logout: '/logout' },
    ]

    const Dropdown = ({ user }) => {
        const handleLogout = async () => {
            try {
                setLoading(true)
                await logout()
            } catch (e) {
                log(e)
            } finally {
                setLoading(false)
            }
        }

        const displayName = String(user?.display_name).split(' ')[0]

        return (
            <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="flex space-x-2 items-center">
                    {user?.ID ? (
                        <div className="font-semibold flex space-x-2 items-center text-olm-black ">
                            <span>{displayName}</span>
                            <ChevronDownIcon className="h-5 w-5   opacity-75" />
                        </div>
                    ) : (
                        <UserIcon className="h-10 w-10" />
                    )}
                </Menu.Button>
                <Menu.Items className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white border z-20">
                    {menuItems.map((item, index) => {
                        const [label, url] = Object.entries(item)[0]
                        return (
                            <Menu.Item key={index}>
                                {({ active }) => (
                                    <MenuItemAction
                                        {...{
                                            active,
                                            handleLogout,
                                            label,
                                            url,
                                        }}
                                    />
                                )}
                            </Menu.Item>
                        )
                    })}
                </Menu.Items>
            </Menu>
        )
    }

    // Show consistent loading state until client-side mounting is complete
    if (!isMounted) {
        return (
            <div className="">
                <Link href="/login">
                    <a className="flex space-x-2 items-center">
                        <UserIcon className="h-6 w-6" />
                    </a>
                </Link>
            </div>
        )
    }

    return (
        <div className="">
            {loading && <Loader text="Logging out..." />}
            {user?.ID ? (
                <Dropdown customer={user.customer} user={user} />
            ) : (
                <Link href={link}>
                    <a className="flex space-x-2 items-center">
                        <UserIcon className="h-6 w-6" />
                    </a>
                </Link>
            )}
        </div>
    )
}

export default LoginButton
